import { useEffect, useState } from "react";

/**
 * Reactive ``matchMedia(query)`` value.
 *
 * Used for the responsive sidebar break — < 768px the sidebar
 * collapses into an overlay drawer, ≥ 768px it sits inline. The
 * listener is registered once per query so multiple consumers of the
 * same breakpoint share zero allocation.
 *
 * SSR-safe: defaults to ``false`` on the server (no ``window``), so
 * the first hydrated render assumes the desktop layout. The effect
 * then corrects to the real value on the client. This matches what
 * the rest of the SPA does (no flash because we don't SSR the SPA
 * pages anyway — they ship from ``index.html`` post-mount).
 */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia(query);
    const onChange = (e: MediaQueryListEvent) => setMatches(e.matches);
    // Sync once in case of late mount.
    setMatches(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, [query]);

  return matches;
}
